import React, { useEffect, useState } from "react";
import { api, formatErr, downloadCsv } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { DollarSign, Printer, Download, AlertCircle, Banknote } from "lucide-react";

export default function CommissionPayout() {
  const { currentCompany } = useAuth();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/commission-payout", { params: { month } });
      setData(d);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const exportCsv = () => {
    if (!data?.reps?.length) return;
    const rows = [
      [`كشف عمولات المندوبين — ${month} — ${data.company_name}`],
      [`معدل العمولة: ${data.commission_rate}% · العملة: ${data.currency}`],
      [],
      ["المندوب", "الهاتف", "الإيميل", "IBAN", "عدد الفواتير", "المبيعات", "المرتجعات", "الصافي", "العمولة"],
      ...data.reps.map(r => [r.name, r.phone, r.email, r.iban, r.count, r.sales, r.returns, r.net, r.commission]),
      [],
      ["الإجمالي", "", "", "", "", "", "", "", data.total_commission],
    ];
    downloadCsv(`commission_${month}.csv`, rows);
    toast.success("تم التصدير — استخدمه لمعالجة التحويلات البنكية");
  };

  const printReport = () => {
    if (!data?.reps?.length) return;
    const w = window.open("", "_blank");
    const rows = data.reps.map((r, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td><b>${r.name}</b><br><small style="color:#666">${r.phone || ""} ${r.iban ? "· " + r.iban : ""}</small></td>
      <td class="e">${r.count}</td>
      <td class="e">${r.sales.toLocaleString("ar-EG")}</td>
      <td class="e" style="color:#B45309">${r.returns.toLocaleString("ar-EG")}</td>
      <td class="e">${r.net.toLocaleString("ar-EG")}</td>
      <td class="e" style="color:#059669;font-weight:bold;font-size:14px">${r.commission.toLocaleString("ar-EG")}</td>
      <td class="c" style="width:80px"><div style="border:1px dashed #999;height:24px;padding:2px 4px;font-size:9px;color:#999">توقيع</div></td>
    </tr>`).join("");
    w.document.write(`<html dir="rtl"><head><title>كشف عمولات ${month}</title>
      <style>
        body{font-family:Cairo,Arial;padding:20px;color:#000}
        h1{color:#1E3A8A;margin:0;font-size:22px}
        .sub{color:#64748B;font-size:12px;margin-top:2px}
        .head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:3px double #1E3A8A}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #E2E8F0;padding:6px 8px;font-size:12px;text-align:start}
        th{background:#F1F5F9;font-size:11px}
        .c{text-align:center}
        .e{text-align:end;font-variant-numeric:tabular-nums}
        .total-row{background:#DCFCE7;font-weight:bold;font-size:14px}
        .footer{margin-top:24px;display:flex;justify-content:space-between;font-size:12px}
        .sig{border-top:1px solid #000;padding-top:4px;text-align:center;width:180px}
        @page { size: A4 landscape; margin: 12mm; }
      </style></head><body>
      <div class="head">
        <div>
          <h1>كشف عمولات المندوبين</h1>
          <div class="sub">${data.company_name} · شهر ${month}</div>
          <div class="sub">معدل العمولة: ${data.commission_rate}% · العملة: ${data.currency}</div>
        </div>
        <div class="sub">تاريخ الإصدار: ${new Date().toLocaleDateString("ar-EG")}</div>
      </div>
      <table>
        <thead><tr>
          <th class="c">#</th><th>المندوب</th><th class="e">فواتير</th><th class="e">المبيعات</th>
          <th class="e">المرتجعات</th><th class="e">الصافي</th><th class="e">العمولة</th><th class="c">التوقيع</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="total-row">
          <td colspan="6" class="e">الإجمالي المستحق</td>
          <td class="e">${data.total_commission.toLocaleString("ar-EG")} ${data.currency}</td>
          <td></td>
        </tr></tfoot>
      </table>
      <div class="footer">
        <div class="sig">توقيع المحاسب</div>
        <div class="sig">توقيع المدير العام</div>
        <div class="sig">تاريخ الاستلام</div>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
  };

  return (
    <div className="space-y-6" data-testid="commission-payout-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <Banknote className="w-6 h-6 text-emerald-600" /> كشف عمولات المندوبين
          </h1>
          <p className="text-sm text-muted-foreground mt-1">جاهز للطباعة والاعتماد والتحويل البنكي</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">الشهر</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="month-input" />
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!data?.reps?.length} className="gap-1 h-9" data-testid="export-csv-btn"><Download className="w-4 h-4" /> Excel</Button>
          <Button onClick={printReport} disabled={!data?.reps?.length} className="gap-1 h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="print-btn"><Printer className="w-4 h-4" /> طباعة</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-16 text-sm">جاري التحميل...</div>
      ) : !data?.reps?.length ? (
        <Card className="p-12 text-center border-dashed">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <div className="text-muted-foreground">لا توجد عمولات في هذا الشهر</div>
        </Card>
      ) : (
        <>
          <Card className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-emerald-700">إجمالي المستحقات لهذا الشهر</div>
                <div className="text-3xl font-bold text-emerald-800 tabular-nums mt-1" data-testid="total-commission">{data.total_commission.toLocaleString("ar-EG")} {data.currency}</div>
              </div>
              <DollarSign className="w-12 h-12 text-emerald-500/40" />
            </div>
          </Card>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-start">#</TableHead>
                  <TableHead className="text-start">المندوب</TableHead>
                  <TableHead className="text-start">الاتصال</TableHead>
                  <TableHead className="text-end">فواتير</TableHead>
                  <TableHead className="text-end">المبيعات</TableHead>
                  <TableHead className="text-end">المرتجعات</TableHead>
                  <TableHead className="text-end">الصافي</TableHead>
                  <TableHead className="text-end">العمولة</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.reps.map((r, i) => (
                  <TableRow key={r.id} data-testid={`rep-row-${i}`}>
                    <TableCell><span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono">#{i + 1}</span></TableCell>
                    <TableCell className="font-semibold">{r.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.phone && <div>{r.phone}</div>}
                      {r.iban && <div className="font-mono text-[10px]">{r.iban}</div>}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">{r.count}</TableCell>
                    <TableCell className="text-end tabular-nums">{r.sales.toLocaleString("ar-EG")}</TableCell>
                    <TableCell className="text-end tabular-nums text-amber-700">{r.returns.toLocaleString("ar-EG")}</TableCell>
                    <TableCell className="text-end tabular-nums">{r.net.toLocaleString("ar-EG")}</TableCell>
                    <TableCell className="text-end tabular-nums font-bold text-emerald-700 text-lg">{r.commission.toLocaleString("ar-EG")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
